import { EmptyError, firstValueFrom, Observable } from 'rxjs';

/**
 * UNIVAPLAT-3639. SampleInterceptor turns 401/403/404/500 into `of(err.message)` after it has already
 * toasted or redirected. HttpClient drops that string from a body stream, so `firstValueFrom()` on the
 * call rejects with EmptyError. Nothing is left to report, so this EmptyError is noise.
 *
 * Narrow on purpose: EmptyError only. The name/message check covers an error that crossed a realm or
 * bundle boundary, where `instanceof` fails.
 */
const EMPTY_ERROR_NAME = 'EmptyError';
const EMPTY_ERROR_MESSAGE = 'no elements in sequence';

export function isEmptyError(error: unknown): boolean {
    if (error instanceof EmptyError) {
        return true;
    }
    const candidate = error as { name?: unknown; message?: unknown } | null | undefined;
    return (
        typeof candidate === 'object' &&
        candidate !== null &&
        candidate.name === EMPTY_ERROR_NAME &&
        candidate.message === EMPTY_ERROR_MESSAGE
    );
}

/**
 * Like `firstValueFrom`, but a stream that completes without a value (an HTTP error the interceptor
 * already handled) resolves to `fallback` instead of rejecting with EmptyError. Any other error still
 * rejects.
 *
 * Pass a fallback the caller can render safely (`[]`, an empty page). Without one the promise resolves
 * `undefined`: only use that where every caller guards for it, and never for a mutation, where
 * `undefined` would read as success.
 */
export function firstValueFromSafe<T>(source: Observable<T>): Promise<T | undefined>;
export function firstValueFromSafe<T>(source: Observable<T>, fallback: T): Promise<T>;
export function firstValueFromSafe<T>(source: Observable<T>, fallback?: T): Promise<T | undefined> {
    return firstValueFrom(source, { defaultValue: fallback });
}
